import { House, UserRound, WalletCards } from 'lucide-react';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from '@/components/ui/link';
import { useLanguage } from '@/i18n/language-context';
import { useDeveloperFeatures } from '@/lib/developer-features';
import { useGiving } from './giving';
import { useSession } from './session';
import { useScrollHidden } from './use-scroll-hidden';

const labels = {
  ro: { home: 'Acasă', gifts: 'Daruri', account: 'Cont', navigation: 'Navigare principală' },
  en: { home: 'Home', gifts: 'Gifts', account: 'Account', navigation: 'Main navigation' },
  hu: { home: 'Kezdőlap', gifts: 'Adományok', account: 'Fiók', navigation: 'Fő navigáció' },
};

export function MobileDonorNavigation(): JSX.Element {
  const { locale } = useLanguage();
  const copy = labels[locale];
  const { pathname, key } = useLocation();
  const scrollHidden = useScrollHidden(pathname === '/basket', key);
  const { draft } = useGiving();
  const session = useSession();
  const [pressedTab, setPressedTab] = useState<number | null>(null);
  const [pointerFocusedTab, setPointerFocusedTab] = useState<number | null>(null);
  const gifts = draft.items.length;
  const destinations = [
    {
      to: '/',
      label: copy.home,
      icon: House,
      active: pathname === '/' || pathname.startsWith('/o/'),
      count: 0,
    },
    {
      to: '/basket',
      label: copy.gifts,
      icon: WalletCards,
      active:
        pathname === '/basket' ||
        pathname.startsWith('/checkout/') ||
        pathname.startsWith('/give/'),
      count: gifts,
    },
    {
      to: session.profile ? '/account' : '/sign-in',
      label: copy.account,
      icon: UserRound,
      active:
        pathname.startsWith('/account') ||
        pathname.startsWith('/auth/') ||
        pathname.startsWith('/me/') ||
        pathname === '/sign-in',
      count: 0,
    },
  ];
  const activeTab = destinations.findIndex(({ active }) => active);
  return (
    <nav
      className="giving-bottom-nav"
      aria-label={copy.navigation}
      data-active-tab={activeTab}
      data-scroll-hidden={scrollHidden || undefined}
    >
      <span className="giving-bottom-nav__indicator" aria-hidden="true" />
      {destinations.map(({ to, label, icon: Icon, active, count }, index) => (
        <Link
          appearance="bare"
          className="giving-bottom-nav__item"
          key={label}
          to={to}
          aria-current={active ? 'page' : undefined}
          aria-label={count ? `${label} (${count})` : label}
          data-pressed={pressedTab === index || undefined}
          data-pointer-focus={pointerFocusedTab === index || undefined}
          onPointerDown={(event) => {
            if (event.button === 0) {
              setPressedTab(index);
              setPointerFocusedTab(index);
            }
          }}
          onKeyDown={() => setPointerFocusedTab(null)}
          onBlur={() => setPointerFocusedTab(null)}
          onPointerUp={() => setPressedTab(null)}
          onPointerCancel={() => setPressedTab(null)}
          onPointerLeave={() => setPressedTab(null)}
        >
          <span className="giving-bottom-nav__icon">
            <Icon size={23} strokeWidth={active ? 2.1 : 1.7} aria-hidden="true" />
            {count > 0 && (
              <span className="giving-bottom-nav__badge" aria-hidden="true">
                {count > 99 ? '99+' : count}
              </span>
            )}
          </span>
          <span>{label}</span>
        </Link>
      ))}
    </nav>
  );
}

/** Keep the bar and its sliding glass indicator mounted while tabs navigate. */
export function MobileNavigationLayer(): JSX.Element | null {
  const { donorNavigation } = useDeveloperFeatures();
  const { pathname } = useLocation();
  return donorNavigation && !pathname.startsWith('/p/') && !pathname.startsWith('/checkout/') ? (
    <MobileDonorNavigation />
  ) : null;
}
